import api from "../api";
import Vue from "vue";

import {
  startAsyncUploadToS3,
  blobUrlToFile,
  uploadThumbnailForVideo,
} from "../utils/uploadToAmplify";

const state = {
  videos: [],
  videoLibrary: [],
  uploadVideos: {
    videosToUpload: 0,
    videosUploaded: 0,
  },
};
const getters = {
  getVideos: (state) => {
    return state.videos;
  },

  getVideoLibrary(state) {
    return state.videoLibrary;
  },
  numberOfVideosInLibrary(state) {
    return state.videoLibrary.length;
  },
  getUploadVideos(state) {
    return state.uploadVideos;
  },
};

const mutations = {
  addVideo(state, { video, index }) {
    state.videos.splice(index, 0, video);
  },
  updateVideo(state, { video }) {
    const indexOfVideo = state.videos.findIndex(
      (item) => item._id === video._id
    );

    Vue.set(state.videos, indexOfVideo, video);
  },
  updateVideoThumbnail(state, { video }) {
    if (!video) return;

    const idxV = state.videos.findIndex((v) => v._id === video._id);
    if (idxV !== -1) {
      const v = {
        ...state.videos[idxV],
        thumbnail: video.thumbnail,
        thumbnailS3Path: video.thumbnailS3Path,
      };
      Vue.set(state.videos, idxV, v);
    }

    const idxL = state.videoLibrary.findIndex((v) => v._id === video._id);
    if (idxL !== -1) {
      const v = {
        ...state.videoLibrary[idxL],
        thumbnail: video.thumbnail,
        thumbnailS3Path: video.thumbnailS3Path,
      };
      Vue.set(state.videoLibrary, idxL, v);
    }
  },
  updateVideoTrim(state, { video }) {
    const idxV = state.videos.findIndex((v) => v._id === video._id);
    if (idxV !== -1) {
      const v = {
        ...state.videos[idxV],
        trimmedClipStart: video.trimmedClipStart,
        trimmedClipDuration: video.trimmedClipDuration,
      };
      Vue.set(state.videos, idxV, v);
    }

    const idxL = state.videoLibrary.findIndex((v) => v._id === video._id);
    if (idxL !== -1) {
      const v = {
        ...state.videoLibrary[idxL],
        trimmedClipStart: video.trimmedClipStart,
        trimmedClipDuration: video.trimmedClipDuration,
      };
      Vue.set(state.videoLibrary, idxL, v);
    }
  },
  deleteVideo(state, index) {
    if (index <= state.videos.length) {
      state.videos.splice(index, 1);
    }
  },
  async deleteVideoFromLibrary(state, videoId) {
    const index = state.videoLibrary.findIndex(
      (video) => video._id === videoId
    );
    if (index !== -1) {
      state.videoLibrary.splice(index, 1);

      try {
        await api.deleteVideo(videoId);
      } catch (err) {
        console.error("Error deleting video:", err);
      }
    }
  },
  resetVideos(state) {
    state.videos = [];
  },
  setVideos(state, videos) {
    state.videos = videos;
  },

  setVideoLibrary(state, videos) {
    state.videoLibrary = videos;
  },
  VIDEO_UPLOADED(state) {
    state.uploadVideos.videosUploaded += 1;
  },
  VIDEO_TO_UPLOAD(state) {
    state.uploadVideos.videosToUpload += 1;
  },
  RESET_UPLOAD_VIDEOS(state) {
    state.uploadVideos.videosToUpload = 0;
    state.uploadVideos.videosUploaded = 0;
  },
  ADD_VIDEO_TO_LIBRARY(state, video) {
    state.videoLibrary.push(video);
  },
  updateVideoLibraryFileName(state, { videoId, fileName }) {
    const video = state.videoLibrary.find((video) => video._id === videoId);
    if (video) {
      video.fileName = fileName;
    }
  },
  updateVideoLibraryProductIds(state, { videoId, productIds }) {
    const video = state.videoLibrary.find((video) => video._id === videoId);
    if (video) {
      Vue.set(video, "productIds", productIds);
    }
  },
  updateVideoLibraryVideoTitle(state, { videoId, videoTitle }) {
    const video = state.videoLibrary.find((video) => video._id === videoId);
    if (video) {
      Vue.set(video, "videoTitle", videoTitle);
    }
  },
  updateVideoLibraryChapters(state, { videoId, chapters }) {
    const video = state.videoLibrary.find((video) => video._id === videoId);
    if (video) {
      Vue.set(video, "chapters", chapters);
    }
  },
  updateVideoLibraryTags(state, { videoId, tags }) {
    const video = state.videoLibrary.find((v) => v._id === videoId);
    if (video) Vue.set(video, "tags", tags);
  },
  updateVideoLibraryLanguage(state, { videoId, language }) {
    const video = state.videoLibrary.find((v) => v._id === videoId);
    if (video) Vue.set(video, "language", language);
  },
};

const actions = {
  async createVideo({ commit }, { file, index }) {
    // create the campaign first so we retreive the proper document ID. To avoid duplicate key in Mongo
    let video;
    const tempFile = file.tempFile;

    if (file._id) {
      video = file;
      video.alreadyCreated = true;
    } else {
      delete file.tempFile;

      //For gemme bijoux we automatically set text
      if (file.siteId === "516b944e-30ae-49af-9697-8d290f74f635") {
        file.videoTitle = "Voir le produit";
      }

      video = await api.createVideo(file);
    }

    let blobThumbnail;
    if (file.thumbnail && file.thumbnail.includes("blob:http")) {
      // keep the blob url to display the thumbnail in the UI immediately
      blobThumbnail = file.thumbnail;
      commit("updateVideoThumbnail", {
        video: {
          _id: video._id,
          thumbnail: blobThumbnail,
          thumbnailS3Path: video.thumbnailS3Path,
        },
      });

      const thumbnailFile = await blobUrlToFile(file.thumbnail, video.fileName);
      video.tempThumbnailFile = thumbnailFile;

      // upload to S3 and get CDN URL
      const result = await uploadThumbnailForVideo(video);
      const cdnUrl = result.thumbnail.replace(
        "https://image-compression-pipeline-destination.s3.eu-west-1.amazonaws.com",
        process.env.VUE_APP_IMAGE_CDN_URL
      );

      // wait for backend to persist CDN thumbnail URL
      const updatedWithThumbnail = await api.updateVideo(video._id, {
        thumbnail: cdnUrl,
        thumbnailS3Path: result.thumbnailS3Path,
      });

      // update store with CDN thumbnail
      commit("updateVideoThumbnail", { video: updatedWithThumbnail });
      delete video.tempThumbnailFile;
      video.thumbnail = cdnUrl;
    }

    file.thumbnail && (video.thumbnail = file.thumbnail);
    blobThumbnail && (video.thumbnail = blobThumbnail);

    // add video entry to client state
    commit("addVideo", { video, index });

    if (tempFile) {
      commit("VIDEO_TO_UPLOAD");
      const res = await startAsyncUploadToS3(video, tempFile);
      if (res.status === "success") {
        commit("VIDEO_UPLOADED");

        // after file upload, update the DB with real S3 URL
        if (res.url) {
          const updatedWithUrl = await api.updateVideo(video._id, {
            url: res.url,
          });
          commit("updateVideo", { video: updatedWithUrl });
        }
      }
    } else {
      commit("VIDEO_TO_UPLOAD");
      commit("VIDEO_UPLOADED");
    }

    return video;
  },
  async getVideo({ commit }, videoId) {
    try {
      const video = await api.getVideo(videoId);
      commit("addVideo", video);
    } catch (err) {
      throw err;
    }
  },
  async updateVideoThumbnail(
    { commit },
    { videoId, dataToUpdate, blobThumbnail = null }
  ) {
    try {
      const videoUpdated = await api.updateVideo(videoId, dataToUpdate);
      // on step 2 we want the blob because compression can take time and we want to show the user the thumbnail as soon as possible
      blobThumbnail && (videoUpdated.thumbnail = blobThumbnail);
      await commit("updateVideoThumbnail", { video: videoUpdated });
      return {
        status: "success",
        res: "video.updated",
        id: videoUpdated._id,
      };
    } catch (err) {
      throw new Error(err);
    }
  },
  async updateVideoTrim({ commit }, { videoId, clipStart, clipDuration }) {
    const videoUpdated = await api.updateVideoTrimChunks({
      videoId,
      clipStart,
      clipDuration,
    });

    await commit("updateVideoTrim", { video: videoUpdated });
  },
  async getVideoLibrary({ commit }, data) {
    const videos = await api.getVideos(data);
    commit("setVideoLibrary", videos);
  },
  async deleteVideoFromLibrary({ commit }, videoId) {
    commit("deleteVideoFromLibrary", videoId);
  },
  async addVideoToLibrary({ commit }, video) {
    commit("ADD_VIDEO_TO_LIBRARY", video);
  },
  async updateVideoLibraryFileName({ commit }, { videoId, fileName }) {
    try {
      await api.updateVideo(videoId, { fileName });
      commit("updateVideoLibraryFileName", { videoId, fileName });

      return {
        status: "success",
        message: "File name updated successfully",
      };
    } catch (err) {
      throw new Error(err);
    }
  },
  async updateVideo({ commit }, video) {
    try {
      commit("updateVideo", { video });
    } catch (err) {
      throw new Error(err);
    }
  },
  async updateVideoProductIds({ commit }, { videoId, productIds }) {
    try {
      await api.updateVideo(videoId, { productIds });
      commit("updateVideoLibraryProductIds", { videoId, productIds });
      return {
        status: "success",
        message: "Product IDs updated successfully",
      };
    } catch (err) {
      throw new Error(err);
    }
  },
  async updateVideoTitle({ commit }, { videoId, videoTitle }) {
    try {
      await api.updateVideo(videoId, { videoTitle });

      const video = { _id: videoId, videoTitle };
      commit("updateVideoLibraryVideoTitle", { videoId, videoTitle });

      return {
        status: "success",
        message: "Video title updated successfully",
      };
    } catch (err) {
      throw new Error(err);
    }
  },
  async updateVideoChapters({ commit }, { videoId, chapters }) {
    try {
      await api.updateVideo(videoId, { chapters });
      commit("updateVideoLibraryChapters", { videoId, chapters });
      return { status: "success" };
    } catch (err) {
      throw new Error(err);
    }
  },
  async updateVideoTags({ commit }, { videoId, tags }) {
    try {
      await api.updateVideo(videoId, { tags });
      commit("updateVideoLibraryTags", { videoId, tags });
      return { status: "success" };
    } catch (err) {
      throw new Error(err);
    }
  },
  async updateVideoLanguage({ commit }, { videoId, language }) {
    try {
      await api.updateVideo(videoId, { language });
      commit("updateVideoLibraryLanguage", { videoId, language });
      return { status: "success" };
    } catch (err) {
      throw new Error(err);
    }
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
